import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import round from "lodash/round";

import { Spinner } from "@/components/ui/spinner";
import useTheme from "hooks/useTheme";
import CustomizedLegend from "routes/dashboard/CustomizedLegend";
import { chartColor } from "routes/dashboard/CallDashboardHelpers";
import { useCallDashboardFilters } from "routes/dashboard/useCallDashboardFilters";
import DashboardService from "services/Dashboard";

const getBreakdownByDialSession = async ({ queryKey = [] }) => {
  const [, filters] = queryKey;
  const resp = await DashboardService.getBreakdownByDialSession(filters);
  if (!resp.data?.length) {
    return [{ name: "No Results", total: 0 }];
  }
  return resp.data;
};

function CallsBreakDownByDialSessionChart() {
  const [queryFilters, setFilters] = useCallDashboardFilters();
  const { data, isFetching } = useQuery({
    initialData: [],
    queryKey: ["call-dashboard-breakdown-by-dial-session", queryFilters],
    queryFn: getBreakdownByDialSession,
  });
  const [disabled, setDisabled] = useState({});
  const { isDark } = useTheme();
  const axisColor = isDark ? "#b0b0b0" : "#666";
  const gridColor = isDark ? "#404040" : "#ccc";
  const textColor = isDark ? "#e8e8e8" : "#000";

  const { displayData, total } = useMemo(() => {
    const dd = data.map((item, i) => ({
      ...item,
      total: disabled[item.name] ? 0 : item.total,
      fill: chartColor(i, data.length, isDark),
    }));
    return {
      displayData: dd,
      total: dd.reduce((acc, c) => acc + Number(c.total), 0),
    };
  }, [data, disabled, isDark]);

  const handleLegendClick = dataKey => {
    if (total === 0) return;
    setDisabled(prev => {
      if (prev[dataKey]) {
        const { [dataKey]: _, ...rest } = prev;
        return rest;
      }
      if (Object.keys(prev).length + 1 === data.length) return prev;
      return { ...prev, [dataKey]: true };
    });
  };

  const handleDataClick = dataItem => {
    if (total === 0) return;
    setFilters({ dial_session_ids: [dataItem.id] });
  };

  const renderTooltip = ({ active, payload }) => {
    if (!(active && payload && payload.length)) return null;
    const tooltipClasses =
      "tw:rounded tw:bg-[var(--color-surface-overlay)] tw:p-[5px] tw:text-[var(--color-text-primary)]";
    if (total === 0) return <div className={tooltipClasses}>No Results</div>;
    const item = payload[0];
    const percentage = round((item.value / total) * 100, 2) || 0;
    return (
      <div className={tooltipClasses}>
        {item.payload.name}: {item.value} ({percentage}%)
      </div>
    );
  };

  return (
    <div className="tw:relative tw:h-full tw:w-full">
      {isFetching && (
        <Spinner className="tw:absolute tw:top-1/2 tw:left-1/2 tw:z-10 tw:size-8 tw:-translate-x-1/2 tw:-translate-y-1/2" />
      )}
      <ResponsiveContainer>
        <BarChart data={displayData} layout="vertical" barCategoryGap={1}>
          <XAxis type="number" dataKey="total" tick={{ fill: axisColor }} />
          <YAxis
            type="category"
            dataKey="name"
            width={140}
            tick={{ fill: axisColor }}
          />
          <Bar dataKey="total" onClick={handleDataClick} />
          <Tooltip cursor={false} content={renderTooltip} />
          <CartesianGrid horizontal={false} stroke={gridColor} />
          <Legend
            layout="horizontal"
            verticalAlign="bottom"
            content={legendProps => (
              <CustomizedLegend
                {...legendProps}
                disabled={disabled}
                textColor={textColor}
                onItemClick={handleLegendClick}
              />
            )}
            payload={displayData.map(item => ({
              dataKey: item.name,
              color: item.fill,
            }))}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default CallsBreakDownByDialSessionChart;
